/* eslint-disable react/jsx-handler-names */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';

import withMainControls from 'eventbrite_design_system/structure/hoc/withMainControls';

import { withEventSaveNotification } from '@eb/destination-utils';
import { setPageLabel, setCustomData } from '@eb/performance-monitor';

import { trackPageView as trackPageViewAction } from '../actions/analytics';

import { setSearchInformationFromUrl as setSearchInformationFromUrlAction } from '../actions/location';
import {
    handleApplyFilter as handleApplyFilterAction,
    handleRemoveFilter as handleRemoveFilterAction,
    updateSearchInformationFromUrl as updateSearchInformationFromUrlAction,
} from '../actions/search';

import { getSelectedFilterCount, getResetLink } from '../selectors/search';

import FilterPanel from '../components/filterPanel/FilterPanel';
import FilterPanelMobile from '../components/filterPanel/FilterPanelMobile';
import ConnectedResultsPanel from './ConnectedResultsPanel';
import ConnectedMapPanel from './ConnectedMapPanel';
import SearchFooterContainer from './SearchFooterContainer';

import {
    LUX_LABEL_SEARCH,
    GOOGLE_OPTIMIZE_NULL_EXPERIMENT_ID,
} from '../constants';

import './SearchContainer.scss';

export class SearchBaseScreen extends React.PureComponent {
    static propTypes = {
        /**
         * GA Utility for tracking the pageview of search
         */
        onTrackPageViewAction: PropTypes.func.isRequired,
        /**
         * Link to reset the current filters
         */
        resetLink: PropTypes.string.isRequired,
        /**
         * Number of filters currently applied to the search experience
         */
        selectedFilterCount: PropTypes.number.isRequired,
        /**
         * Sets current search information state from the URL
         */
        setSearchInformationFromUrl: PropTypes.func.isRequired,
        /**
         * Gets current search information state from the URL
         */
        updateSearchInformationFromUrl: PropTypes.func.isRequired,
        /**
         * Callback for when filters are applied
         */
        handleApplyFilter: PropTypes.func,
        /**
         * Callback for when filters are removed
         */
        handleRemoveFilter: PropTypes.func,
        /**
         * History object from React Router
         */
        // eslint-disable-next-line
        history: PropTypes.object,
        /**
         * Type of individual filter to show in the filter panel
         * or '' if all filters must be shown
         */
        filterTypeToShow: PropTypes.string,
    };

    componentDidMount() {
        const {
            onTrackPageViewAction,
            setSearchInformationFromUrl,
        } = this.props;

        // set the pair location-searchCriterias so we can use them later when
        // the user try to navigate back
        setSearchInformationFromUrl(
            this.props.history.location.pathname,
            queryString.parse(this.props.history.location.search),
        );

        // track pageview GA action
        onTrackPageViewAction();
        setPageLabel(LUX_LABEL_SEARCH);
        setCustomData('search_version', 'eb-spa');

        if (window.gtag) {
            window.gtag('event', 'optimize.callback', {
                name: GOOGLE_OPTIMIZE_NULL_EXPERIMENT_ID,
                callback: (value, name) =>
                    this._handleOptimizeCallback(value, name),
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.location.pathname + prevProps.location.search !==
            this.props.location.pathname + this.props.location.search
        ) {
            this.props.updateSearchInformationFromUrl(
                this.props.location.pathname,
                queryString.parse(this.props.location.search.replace('?', '')),
            );
        }
    }

    _handleOptimizeCallback = () => null;

    render() {
        const {
            handleApplyFilter,
            handleRemoveFilter,
            selectedFilterCount,
            resetLink,
            isFilterPanelMobileOpen,
            filterTypeToShow,
        } = this.props;

        return (
            <main
                className={classnames('search-base-screen__root', {
                    'search-base-screen--is-mobile-filter-open': isFilterPanelMobileOpen,
                })}
            >
                <aside className="search-base-screen__filter-panel">
                    <FilterPanel
                        handleApplyFilter={handleApplyFilter}
                        handleRemoveFilter={handleRemoveFilter}
                        selectedFilterCount={selectedFilterCount}
                        resetLink={resetLink}
                    />
                </aside>
                {isFilterPanelMobileOpen && (
                    <div>
                        <FilterPanelMobile
                            handleApplyFilter={handleApplyFilter}
                            handleRemoveFilter={handleRemoveFilter}
                            selectedFilterCount={selectedFilterCount}
                            resetLink={resetLink}
                            filterTypeToShow={filterTypeToShow}
                        />
                        {/* eslint-disable-next-line react/no-danger */}
                        <style
                            dangerouslySetInnerHTML={{
                                __html: `
                          .eds-structure {
                            position: fixed;
                            width: 100%;
                            height: 100%
                          }
                        `,
                            }}
                        />
                    </div>
                )}
                <section className="search-base-screen__search-panel">
                    <ConnectedResultsPanel />
                    <SearchFooterContainer />
                </section>

                <section className="search-base-screen__map-panel">
                    <ConnectedMapPanel />
                </section>
            </main>
        );
    }
}

const _mapStateToProps = (state) => ({
    selectedFilterCount: getSelectedFilterCount(state),
    resetLink: getResetLink(state),
    isFilterPanelMobileOpen: state.app.isFilterPanelMobileOpen,
    filterTypeToShow: state.app.filterTypeToShow,
});

const _mapDispatchToProps = (dispatch, ownProps) => ({
    handleApplyFilter: (...args) =>
        dispatch(handleApplyFilterAction(ownProps.history, ...args)),
    handleRemoveFilter: (...args) =>
        dispatch(handleRemoveFilterAction(ownProps.history, ...args)),
    setSearchInformationFromUrl: (...args) =>
        dispatch(setSearchInformationFromUrlAction(...args)),
    updateSearchInformationFromUrl: (...args) =>
        dispatch(updateSearchInformationFromUrlAction(...args)),
    onTrackPageViewAction: (...args) => dispatch(trackPageViewAction(...args)),
});

const enhance = compose(
    withMainControls,
    withRouter,
    connect(_mapStateToProps, _mapDispatchToProps),
    withEventSaveNotification,
);

export default enhance(SearchBaseScreen);
